import { useState, useEffect, useCallback, useRef } from 'react';
import useSWR from 'swr';
// Resources
import {
  getBlockchainConfigurations,
  getNftCollections,
} from '@/resources/nft-service.resource';

function buildBlockchainCollections(collections, blockchainConfigurations) {
  const list = Array.isArray(collections) ? collections : [];
  const configs = Array.isArray(blockchainConfigurations) ? blockchainConfigurations : [];
  const data = [];
  for (let i = 0; i < list.length; i++) {
    const collection = list[i];
    const chain = configs.find((config) => config.chainId === collection.chainId);
    if (!chain) {
      console.warn(
        '[useNftCollections] No blockchainConfiguration for collection chainId',
        collection.chainId,
        collection.contractAddress
      );
      continue;
    }
    data.push({
      ...collection,
      contractAddress: collection.contractAddress.toLowerCase(),
      networkName: chain.name,
      rpcUrl: chain.rpcUrl,
      blockExplorerUrl: chain.blockExplorerUrl,
      nativeTokenDecimalPrecision: chain.nativeTokenDecimalPrecision,
      usesEIP1559: chain.usesEIP1559,
      isDefaultLayer1: chain.isDefaultLayer1,
      isDefaultLayer2: chain.isDefaultLayer2,
    });
  }
  return data;
}

async function getCollections() {
  const collectionsResponse = await fetch(getNftCollections().url);
  const collectionsPayload = await collectionsResponse.json();
  const collections = Array.isArray(collectionsPayload)
    ? collectionsPayload
    : (collectionsPayload?.collections ??
      collectionsPayload?.data ??
      collectionsPayload?.value ??
      []);

  const blockchainsResponse = await fetch(getBlockchainConfigurations().url);
  const blockchainsPayload = await blockchainsResponse.json();
  const blockchainConfigurations =
    blockchainsPayload?.blockchainConfigurations ?? blockchainsPayload?.data ?? [];

  return buildBlockchainCollections(collections, blockchainConfigurations);
}

const fetcherSWR = async (url, collections, walletInfo) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ collections, walletInfo }),
  });
  return response.json();
};

export function useNftCollections({ walletInfo }) {
  const [collections, setCollections] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState({
    header: null,
    body: null,
  });
  const [collectionsList, setCollectionsList] = useState([]);

  const [nftFetchEnabled, setNftFetchEnabled] = useState(false);
  const nftFetchEnabledRef = useRef(false);

  const reset = () => {
    setCollections({});
    setCollectionsList([]);
    setLoading(true);
    setError({
      header: null,
      body: null,
    });
    localStorage.clear();
  };

  useEffect(() => {
    // Keep loading true while wallet is still loading
    if (walletInfo.loading) {
      return;
    }

    const fetchCollections = async () => {
      try {
        const collections = await getCollections();
        setCollectionsList(collections);
      } catch (err) {
        setError({
          header: 'Error fetching collections',
          body: 'Please try again later',
        });
      }
    };

    fetchCollections();
  }, [walletInfo]);

  const {
    data: collectionData,
    isLoading: collectionDataLoading,
    error: collectionDataError,
    mutate: refreshCollectionsData,
  } = useSWR(
    nftFetchEnabled &&
      collectionsList.length > 0 &&
      walletInfo &&
      (walletInfo.core || walletInfo.legacy)
      ? ['/api/nfts/collectionData', collectionsList, walletInfo]
      : null,
    ([url, collections, walletInfo]) => fetcherSWR(url, collections, walletInfo),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  useEffect(() => {
    if (collectionDataError) {
      setError({
        header: 'Error fetching collections NFTs',
        body: 'Please try again later',
      });
    }
  }, [collectionDataError]);

  useEffect(() => {
    if (collectionData && collectionsList.length > 0) {
      const collectionsMap = {};
      collectionsList.forEach((collection) => {
        const matchingData = collectionData.find(
          (data) =>
            data &&
            (data.contractAddress || '').toLowerCase() ===
              (collection.contractAddress || '').toLowerCase() &&
            data.chainId === collection.chainId
        );
        if (!matchingData) return;

        const hasCoreNfts =
          matchingData.nfts?.core && Object.keys(matchingData.nfts.core).length > 0;
        const hasLegacyNfts =
          matchingData.nfts?.legacy && Object.keys(matchingData.nfts.legacy).length > 0;

        if (hasCoreNfts || hasLegacyNfts) {
          const compositeKey = `${collection.contractAddress}-${collection.chainId}`;
          collectionsMap[compositeKey] = {
            ...collection,
            ...matchingData,
          };
        }
      });
      setCollections(collectionsMap);
      setLoading(false);
    }
  }, [collectionData, collectionsList]);

  useEffect(() => {
    nftFetchEnabledRef.current = nftFetchEnabled;
  }, [nftFetchEnabled]);

  const loadCollectionsData = useCallback(() => {
    if (!nftFetchEnabledRef.current) {
      setLoading(true);
      setNftFetchEnabled(true);
    }
  }, []);

  const fetchAndUpdateSingleCollection = async (collection) => {
    try {
      const response = await fetch('/api/nfts/collectionData', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ collections: [collection], walletInfo }),
      });
      const [data] = await response.json();

      // Only update if there are NFTs in core or legacy
      const hasCoreNfts = data?.nfts?.core && Object.keys(data.nfts.core).length > 0;
      const hasLegacyNfts =
        data?.nfts?.legacy && Object.keys(data.nfts.legacy).length > 0;

      if (hasCoreNfts || hasLegacyNfts) {
        const compositeKey = `${collection.contractAddress}-${collection.chainId}`;
        setCollections((prev) => ({
          ...prev,
          [compositeKey]: {
            ...collection,
            ...data,
          },
        }));
        return {
          ...collection,
          ...data,
        };
      } else {
        const compositeKey = `${collection.contractAddress}-${collection.chainId}`;
        setCollections((prev) => {
          const updated = { ...prev };
          delete updated[compositeKey];
          return updated;
        });
        return null;
      }
    } catch (err) {
      setError({
        header: 'Error fetching collection NFTs',
        body: 'Please try again later',
      });
    }
  };

  return {
    collections,
    loading,
    collectionDataLoading,
    error,
    reset,
    getCollectionByAddress: (key) =>
      collections[key] ??
      (key && !key.includes('-')
        ? Object.values(collections).find(
            (c) => (c.contractAddress || '').toLowerCase() === (key || '').toLowerCase()
          )
        : undefined),
    refreshCollectionsData,
    fetchAndUpdateSingleCollection,
    loadCollectionsData,
  };
}
